'use client'

import { persistWorkspaceId } from '@/lib/tenant/workspace-id-storage';
import { useState, useEffect, Suspense, useSyncExternalStore } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@/lib/zod-resolver'
import { z } from 'zod/v4'
import { Shield, Eye, EyeOff, Loader2, Mail, KeyRound, Fingerprint, Users, ArrowRight } from 'lucide-react'
import { startAuthentication } from '@simplewebauthn/browser'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  getLastUsedMethod,
  recordLastUsedMethod,
  stageSsoAttempt,
  type LoginMethod,
} from '@/lib/auth/last-used-method'
import { toast } from 'sonner'
import { authApi, type LoginResponse } from '@/lib/api/auth'
import { useAuthStore } from '@/stores/auth-store'
import { useTranslation } from '@/lib/i18n'
import { LanguageSwitcher } from '@/components/language-switcher'
import { BrandedLoader } from '@/components/branded-loader'
import { resolveSafeRedirect } from '@/lib/auth/safe-redirect'
import { holdReturnPath } from '@/lib/auth/return-after-sign-in'
import { codeDigits } from '@/lib/auth/one-time-code'
import { SessionEndedNotice } from '@/components/status/session-ended-notice'
import { landedFromLapsedSession } from '@/lib/status/session-ended'

// ── Validation Schemas ──────────────────────────────────────

const loginSchema = z.object({
  email: z.email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

const otpEmailSchema = z.object({
  email: z.email('Please enter a valid email address'),
})

const otpVerifySchema = z.object({
  email: z.email('Please enter a valid email address'),
  // Folded, then counted: `.length(6)` alone passed '١٢٣٤٥٦' through as typed,
  // and the API refused it as 12 bytes against 6.
  otp: z.string().transform(codeDigits).pipe(z.string().length(6, 'OTP must be 6 digits')),
})

type LoginFormData = z.infer<typeof loginSchema>
type OtpEmailData = z.infer<typeof otpEmailSchema>
type OtpVerifyData = z.infer<typeof otpVerifySchema>

// ── Safe redirect helper ────────────────────────────────────

/**
 * Resolve the post-login redirect target from the `next` query param, with
 * open-redirect protection. The producer is the accept-invite page, which
 * sends `?next=<encoded internal path>` so the user lands back on
 * `/accept-invite?token=…` after authenticating (where its sessionStorage
 * pending-token logic finishes the accept).
 *
 * Only SAFE INTERNAL paths are honored: the value must start with a single
 * `/` and must NOT begin with `//` or `/\` (protocol-relative / backslash
 * tricks that browsers treat as absolute external URLs — the classic
 * open-redirect vector). Anything else (absolute `http(s)://`, scheme-relative,
 * empty, or malformed) falls back to the default `/recipients`.
 */
function resolveSafeNext(raw: string | null): string {
  // One rule for every post-auth redirect — the OAuth callback shares it.
  return resolveSafeRedirect(raw, '/dashboard')
}

/**
 * Build the OAuth start URL, telling the backend which host to send the browser BACK to.
 *
 * This one app is served on several product hosts (dash / play / portal / educate). Without
 * `redirectTo`, the callback always bounced to APP_BASE_URL — so an employee signing in on the
 * play/portal host was dumped on the ADMIN console. We pass the origin we actually started on;
 * the backend allowlists it (`AUTH_ALLOWED_REDIRECT_ORIGINS`) and falls back to APP_BASE_URL if
 * it is not trusted, so this can never become an open redirect.
 */
function ssoStartUrl(provider: 'microsoft' | 'google'): string {
  const api = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4001'
  const base = `${api}/v1/auth/${provider}`
  if (typeof window === 'undefined') return base
  return `${base}?redirectTo=${encodeURIComponent(window.location.origin)}`
}

// ── Component ───────────────────────────────────────────────

function LoginPageInner() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { t } = useTranslation()
  const [showPassword, setShowPassword] = useState(false)
  const [otpSent, setOtpSent] = useState(false)
  const [otpEmail, setOtpEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  // SSO loading is PER-PROVIDER, not shared: clicking Google must spin only the
  // Google button, never Microsoft (they used to share `isSubmitting`, so one
  // click lit both). A full-page redirect follows, so we keep the flag set —
  // the spinner stays until the browser navigates away.
  const [ssoLoading, setSsoLoading] = useState<'microsoft' | 'google' | null>(null)

  // Which method this browser signed in with last, for the "Last used" badge.
  //
  // useSyncExternalStore rather than useEffect+setState: it gives React a
  // SERVER snapshot (null — localStorage does not exist there) and a CLIENT
  // snapshot, so the markup hydrates without a mismatch and without the
  // cascading re-render an effect would cause. The value cannot change while
  // this page is mounted — it is only ever written on the way OUT of it — so
  // the subscribe callback has nothing to listen to. getSnapshot returns a
  // string or null, which React compares with Object.is, so it stays stable.
  const lastUsed = useSyncExternalStore<LoginMethod | null>(
    NO_STORE_SUBSCRIBE,
    getLastUsedMethod,
    () => null,
  )
  // Auth-redirect gate. While true, render <BrandedLoader/> instead of the
  // login form so authed users never flash the unauthed UI on direct hits
  // to /login. Closes the "login page shows even when logged in" tester
  // report (2026-05-08). The companion SSR redirect at app/page.tsx covers
  // the / → /login bounce; this gate covers direct /login navigations.
  const [authGateChecking, setAuthGateChecking] = useState(true)

  // Email + Password form
  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  // OTP email form
  const otpEmailForm = useForm<OtpEmailData>({
    resolver: zodResolver(otpEmailSchema),
    defaultValues: { email: '' },
  })

  // OTP verify form
  const otpVerifyForm = useForm<OtpVerifyData>({
    resolver: zodResolver(otpVerifySchema),
    defaultValues: { email: '', otp: '' },
  })

  // Redirect if already authenticated. While the cookie check runs (and
  // while a positive-match redirect is in flight) we render <BrandedLoader/>
  // below instead of the login form so the unauthed UI never flashes for
  // logged-in users.
  useEffect(() => {
    const token = document.cookie.includes('hf-app:access-token')
    if (token) {
      // Already authenticated → honor a safe `next` (e.g. the accept-invite
      // round-trip) instead of always bouncing to /recipients.
      router.push(resolveSafeNext(searchParams.get('next')))
      // Keep the gate `true` so the loader stays on screen until the
      // navigation completes (replacing the page entirely).
      return
    }
    setAuthGateChecking(false)
  }, [router, searchParams])

  // ── Handlers ────────────────────────────────────────────

  /**
   * A login response that is a 2FA CHALLENGE, not a session.
   *
   * Every one of the four sign-in paths on this page can now answer with one
   * (password since RC-E; SSO/OTP/passkey already could). Handing such a
   * response to handleLoginSuccess wrote `accessToken: ''` into localStorage
   * and navigated to the dashboard with no credential — the user saw a broken
   * app instead of a code prompt. Returns true when it routed.
   */
  const routeIfSecondFactorRequired = (response: LoginResponse): boolean => {
    if (!response?.requiresTwoFactor || !response.tempToken) return false
    if (typeof window !== 'undefined') {
      // Same keys /verify-2fa reads and the SSO callback writes.
      sessionStorage.setItem('hf-2fa-email', response.email ?? '')
      sessionStorage.setItem('hf-2fa-temp-token', response.tempToken)
      // /verify-2fa finishes this sign-in, so it takes `next` from here.
      holdReturnPath(searchParams.get('next'))
    }
    router.push('/verify-2fa')
    return true
  }

  const handleLoginSuccess = async (response: LoginResponse) => {
    // Backend returns { user, tokens: { accessToken, refreshToken } }
    // OTP flow returns { accessToken, refreshToken } at top level (no user/tokens wrapper)
    // Password flow returns { user, tokens: { accessToken, refreshToken } }
    const user = (response.user ?? {}) as unknown as Record<string, string>;
    const accessToken = response.tokens?.accessToken ?? response.accessToken ?? '';
    const refreshToken = response.tokens?.refreshToken ?? response.refreshToken ?? '';

    // A token-less "success" is never a success. This used to write '' into
    // localStorage and carry on, which is what made a 2FA challenge look like a
    // broken dashboard rather than a missing step.
    if (!accessToken) {
      throw new Error('Sign-in did not return an access token.')
    }

    // Store tokens immediately so we can make authenticated requests
    if (typeof window !== 'undefined') {
      localStorage.setItem('hf-app:access-token', accessToken);
      localStorage.setItem('hf-app:refresh-token', refreshToken);
    }

    // Fetch workspaces to get the workspace ID (not returned in login)
    let workspaceId: string | undefined;
    try {
      const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4001';
      const wsResponse = await fetch(`${API_URL}/v1/workspaces/my`, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });
      if (wsResponse.ok) {
        const workspaces = await wsResponse.json();
        const wsList = Array.isArray(workspaces) ? workspaces : workspaces.data || [];
        if (wsList.length > 0) {
          workspaceId = wsList[0].id;
          // BOTH channels: the server side reads the cookie, and only the
          // cookie. A localStorage-only write here left every route handler on
          // whatever workspace the JWT claim happened to name.
          persistWorkspaceId(workspaceId!);
        }
      }
    } catch {
      // Workspace fetch failed — proceed without it
    }

    const loginData = {
      user: user as unknown as Parameters<ReturnType<typeof useAuthStore.getState>['login']>[0]['user'],
      accessToken,
      refreshToken,
      workspaceId,
    };
    useAuthStore.getState().login(loginData);
    toast.success(t('auth.loginSuccess'), {
      description: t('auth.welcomeBackName', { name: user?.firstName || user?.email || 'there' }),
    });
    // Accounts provisioned with a one-time temp password must set a real
    // password before doing anything else (takes precedence over `next`).
    // /auth/set-password finishes this sign-in, so it takes `next` from here.
    if (user?.mustChangePassword) {
      holdReturnPath(searchParams.get('next'));
      router.push('/auth/set-password');
      return;
    }
    // Honor a safe `next` (e.g. accept-invite → login → return-and-accept)
    // instead of always landing on /recipients. Open-redirect-guarded.
    router.push(resolveSafeNext(searchParams.get('next')));
  }

  const onLoginSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true)
    try {
      const response = await authApi.login(data.email, data.password)
      // A 2FA-enrolled account gets a challenge, not a session (RC-E).
      if (routeIfSecondFactorRequired(response)) return
      // Confirmed sign-in — safe to remember. Recorded per method rather than
      // inside handleLoginSuccess, which all three paths share.
      recordLastUsedMethod('password')
      await handleLoginSuccess(response)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed. Please check your credentials.'
      toast.error(t('auth.loginFailed'), { description: message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onSendOtp = async (data: OtpEmailData) => {
    setIsSubmitting(true)
    try {
      await authApi.sendOtp(data.email)
      setOtpEmail(data.email)
      setOtpSent(true)
      otpVerifyForm.setValue('email', data.email)
      toast.success(t('auth.otpSent'), {
        description: t('auth.otpSentDesc', { email: data.email }),
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : t('auth.failedSendOtp')
      toast.error(t('auth.failedSendOtp'), { description: message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onVerifyOtp = async (data: OtpVerifyData) => {
    setIsSubmitting(true)
    try {
      const response = await authApi.verifyOtp(data.email, data.otp)
      if (routeIfSecondFactorRequired(response)) return
      // Confirmed sign-in — safe to remember. Recorded per method rather than
      // inside handleLoginSuccess, which all three paths share.
      recordLastUsedMethod('otp')
      await handleLoginSuccess(response)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Invalid or expired OTP'
      toast.error(t('auth.verificationFailed'), { description: message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onPasskeyLogin = async () => {
    setIsSubmitting(true)
    try {
      // 1. Get authentication options from backend
      const { options } = await authApi.passkeyLoginOptions()
      // 2. Trigger browser biometric prompt
      const authResponse = await startAuthentication({ optionsJSON: options as unknown as Parameters<typeof startAuthentication>[0]['optionsJSON'] })
      // 3. Send response to backend for verification + JWT
      const response = await authApi.passkeyLoginVerify(authResponse as unknown as Parameters<typeof authApi.passkeyLoginVerify>[0])
      // Passkey login is usernameless, so the API had to start returning
      // `email` on its challenge for /verify-2fa to be reachable at all.
      if (routeIfSecondFactorRequired(response)) return
      // Confirmed sign-in — safe to remember. Recorded per method rather than
      // inside handleLoginSuccess, which all three paths share.
      recordLastUsedMethod('passkey')
      await handleLoginSuccess(response)
    } catch (error) {
      const message = error instanceof Error ? error.message : t('auth.passkeyFailed')
      toast.error(t('auth.passkeyFailed'), { description: message })
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Render ──────────────────────────────────────────────

  // Auth gate: while we're checking the cookie (and during a logged-in
  // redirect) show the branded loader so the form never flashes for
  // already-authenticated users. See the useEffect above.
  if (authGateChecking) {
    return <BrandedLoader />
  }

  return (
    // Its own scroller: html and body are overflow:hidden for the app shell
    // (globals.css), so a page outside the shell scrolls only in itself. The
    // card is centred with m-auto, not items-center, so a form taller than
    // the window starts at the top and scrolls instead of being cut off.
    <div
      data-testid="login-page-scroller"
      className="relative h-dvh overflow-y-auto bg-background flex flex-col p-4 sm:p-6"
    >
      {/* Top header cluster — Employee Portal entry + Language Switcher.
          Why this lives here: the admin login is the wrong door for an
          employee; without a visible "go to employee portal" affordance
          they bounce off and call IT. Theme tokens (bg-card, primary,
          muted-foreground) come from the same shadcn token set the portal
          login uses — they render identically in both light + dark mode.
          Why /portal/login: the portal login page handles its own
          ?ws=<workspaceId> query-param + sessionStorage workspace
          resolution chain (see app/(portal)/layout.tsx), so the admin
          login doesn't need to pre-resolve a workspace. */}
      <div className="absolute top-3 end-3 sm:top-4 sm:end-4 flex items-center gap-2">
        <Link
          href="/portal/login"
          className="inline-flex items-center gap-2 rounded-md border border-border bg-card hover:bg-muted text-sm font-medium px-3 py-1.5 text-foreground transition-colors"
          aria-label={t('auth.goToEmployeePortal')}
          data-testid="admin-login-go-to-portal"
        >
          <Users className="h-4 w-4 text-primary" aria-hidden="true" />
          <span className="hidden sm:inline">{t('auth.employeePortal')}</span>
          <ArrowRight className="h-3.5 w-3.5 opacity-60 rtl-flip" aria-hidden="true" />
        </Link>
        <LanguageSwitcher />
      </div>
      <div data-testid="login-page-content" className="w-full max-w-md m-auto pt-10 sm:pt-0">
        {/* Brand Header — or, when a lapsed session sent the person here from a
            page (`next`), the 401 picture saying so. Sign-in below is the same. */}
        {landedFromLapsedSession(searchParams.get('next')) ? (
          <SessionEndedNotice className="mb-6 sm:mb-8" />
        ) : (
          <div className="flex flex-col items-center mb-6 sm:mb-8">
            <div className="flex items-center justify-center h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-primary/10 text-primary mb-3 sm:mb-4">
              <Shield className="h-7 w-7 sm:h-8 sm:w-8" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">{t('auth.brand')}</h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1 text-center px-2">
              {t('auth.tagline')}
            </p>
          </div>
        )}

        {/* Login Card */}
        <Card>
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-xl">{t('auth.welcomeBack')}</CardTitle>
            <CardDescription>
              {t('auth.signInDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* The mid-session cascade lands HERE, on a page whose title reads
                "Welcome back" — which is precisely what the 2026-09-17 report
                described: correct credentials, a welcome, and then the login
                form again with nothing explaining why. The reason arrives as a
                query param because a toast cannot survive the hard navigation
                in lib/api/client.ts. Inline and persistent, not a toast. */}
            {searchParams.get('reason') === 'company_disabled' && (
              <div
                role="alert"
                className="mb-6 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {t('auth.companyDisabled')}
              </div>
            )}
            <Tabs defaultValue="password" className="w-full">
              {/* mt-3 gives the floating "Last used" badge room — without it the badge
                  is clipped by the tab row.
                  Each tab shows a SHORT label (auth.methodTabs.*) at every width and
                  may wrap to a second line, never an ellipsis: at ~131px a tab cut
                  "Email + Pa…" and "OTP (Pass…" on desktop, and phones read two
                  English literals (browser QA as nine roles, 2026-10-06). The full
                  method name stays the tab's accessible name and its tooltip. */}
              <TabsList className="w-full mb-6 mt-3 h-auto items-stretch p-1">
                <TabsTrigger
                  value="password"
                  className={METHOD_TAB_CLASS}
                  aria-label={t('auth.emailPassword')}
                  title={t('auth.emailPassword')}
                >
                  {lastUsed === 'password' && <LastUsedBadge floating />}
                  <KeyRound className="h-3.5 w-3.5 shrink-0" />
                  <span className="min-w-0 break-words">{t('auth.methodTabs.password')}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="otp"
                  className={METHOD_TAB_CLASS}
                  aria-label={t('auth.otpPasswordless')}
                  title={t('auth.otpPasswordless')}
                >
                  {lastUsed === 'otp' && <LastUsedBadge floating />}
                  <Mail className="h-3.5 w-3.5 shrink-0" />
                  <span className="min-w-0 break-words">{t('auth.methodTabs.otp')}</span>
                </TabsTrigger>
                <TabsTrigger
                  value="passkey"
                  className={METHOD_TAB_CLASS}
                  aria-label={t('auth.passkey')}
                  title={t('auth.passkey')}
                >
                  {lastUsed === 'passkey' && <LastUsedBadge floating />}
                  <Fingerprint className="h-3.5 w-3.5 shrink-0" />
                  <span className="min-w-0 break-words">{t('auth.methodTabs.passkey')}</span>
                </TabsTrigger>
              </TabsList>

              {/* Tab 1: Email + Password */}
              <TabsContent value="password" className={TAB_PANEL_FOCUS}>
                <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">{t('auth.email')}</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      autoComplete="email"
                      {...loginForm.register('email')}
                      aria-invalid={!!loginForm.formState.errors.email}
                    />
                    {loginForm.formState.errors.email && (
                      <p className="text-sm text-destructive">
                        {loginForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">{t('auth.password')}</Label>
                      <Link
                        href="/auth/forgot-password"
                        className="text-xs text-muted-foreground hover:text-primary transition-colors"
                      >
                        {t('auth.forgotPassword')}
                      </Link>
                    </div>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder={t('auth.passwordPlaceholder')}
                        autoComplete="current-password"
                        className="pe-10"
                        {...loginForm.register('password')}
                        aria-invalid={!!loginForm.formState.errors.password}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                        // axe button-name (accessibility review, 2026-10-05):
                        // an icon-only toggle says what it does and whether it is on.
                        aria-label={showPassword ? t('shell.inputs.hidePassword') : t('shell.inputs.showPassword')}
                        aria-pressed={showPassword}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" aria-hidden="true" />
                        ) : (
                          <Eye className="h-4 w-4" aria-hidden="true" />
                        )}
                      </button>
                    </div>
                    {loginForm.formState.errors.password && (
                      <p className="text-sm text-destructive">
                        {loginForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <Button type="submit" className="w-full h-11 sm:h-10" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 me-2 animate-spin" />
                        {t('auth.signingIn')}
                      </>
                    ) : (
                      t('auth.signIn')
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* Tab 2: OTP (Passwordless) */}
              <TabsContent value="otp" className={TAB_PANEL_FOCUS}>
                {!otpSent ? (
                  <form onSubmit={otpEmailForm.handleSubmit(onSendOtp)} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="otp-email">{t('auth.email')}</Label>
                      <Input
                        id="otp-email"
                        type="email"
                        placeholder={t('auth.emailPlaceholder')}
                        autoComplete="email"
                        {...otpEmailForm.register('email')}
                        aria-invalid={!!otpEmailForm.formState.errors.email}
                      />
                      {otpEmailForm.formState.errors.email && (
                        <p className="text-sm text-destructive">
                          {otpEmailForm.formState.errors.email.message}
                        </p>
                      )}
                    </div>

                    <p className="text-xs text-muted-foreground">
                      {t('auth.otpDescription')}
                    </p>

                    <Button type="submit" className="w-full h-11 sm:h-10" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 me-2 animate-spin" />
                          {t('auth.sendingOtp')}
                        </>
                      ) : (
                        t('auth.sendOtp')
                      )}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={otpVerifyForm.handleSubmit(onVerifyOtp)} className="space-y-4">
                    <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
                      <p className="text-muted-foreground">
                        {t('auth.otpSentDesc', { email: otpEmail })}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="otp-code">{t('auth.verificationCode')}</Label>
                      <Input
                        id="otp-code"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="000000"
                        maxLength={6}
                        className="text-center text-lg tracking-[0.5em] font-mono"
                        {...otpVerifyForm.register('otp', {
                          // Show the digits that will be sent: ١٢٣ becomes 123 as typed.
                          onChange: (e) => otpVerifyForm.setValue('otp', codeDigits(e.target.value)),
                        })}
                        aria-invalid={!!otpVerifyForm.formState.errors.otp}
                      />
                      {otpVerifyForm.formState.errors.otp && (
                        <p className="text-sm text-destructive">
                          {otpVerifyForm.formState.errors.otp.message}
                        </p>
                      )}
                    </div>

                    <Button type="submit" className="w-full h-11 sm:h-10" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 me-2 animate-spin" />
                          {t('auth.verifying')}
                        </>
                      ) : (
                        t('auth.verifyOtp')
                      )}
                    </Button>

                    <div className="flex items-center justify-center gap-1">
                      <span className="text-xs text-muted-foreground">{t('auth.didntReceiveCode')}</span>
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={() => {
                          setOtpSent(false)
                          otpVerifyForm.reset()
                        }}
                      >
                        {t('auth.sendAgain')}
                      </button>
                    </div>
                  </form>
                )}
              </TabsContent>

              {/* Tab 3: Passkey (Biometric) */}
              <TabsContent value="passkey" className={TAB_PANEL_FOCUS}>
                <div className="space-y-4">
                  <p className="text-xs text-muted-foreground">
                    {t('auth.passkeyDescription')}
                  </p>

                  <Button
                    type="button"
                    className="w-full h-11 sm:h-10"
                    disabled={isSubmitting}
                    onClick={onPasskeyLogin}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 me-2 animate-spin" />
                        {t('auth.verifying')}
                      </>
                    ) : (
                      <>
                        <Fingerprint className="h-4 w-4 me-2" />
                        {t('auth.signInWithPasskey')}
                      </>
                    )}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* OAuth Social Login */}
        <div className="mt-6 space-y-3">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">{t('auth.orContinueWith')}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              variant="outline"
              type="button"
              className="h-11 sm:h-10"
              disabled={ssoLoading !== null}
              onClick={() => {
                setSsoLoading('microsoft')
                // Staged, not recorded: the operator is about to leave the page
                // and may abandon at the provider. /auth/callback promotes this
                // only on a confirmed return.
                stageSsoAttempt('microsoft')
                // /auth/callback finishes this sign-in, so it takes `next` from here.
                holdReturnPath(searchParams.get('next'))
                window.location.href = ssoStartUrl('microsoft')
              }}
            >
              {ssoLoading === 'microsoft' ? (
                <Loader2 className="me-2 h-4 w-4 animate-spin" />
              ) : (
                <svg className="me-2 h-4 w-4" viewBox="0 0 21 21" fill="none">
                  <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                  <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                </svg>
              )}
              {t('auth.microsoft')}
              {lastUsed === 'microsoft' && <LastUsedBadge />}
            </Button>
            <Button
              variant="outline"
              type="button"
              className="h-11 sm:h-10"
              disabled={ssoLoading !== null}
              onClick={() => {
                setSsoLoading('google')
                // Staged, not recorded: the operator is about to leave the page
                // and may abandon at the provider. /auth/callback promotes this
                // only on a confirmed return.
                stageSsoAttempt('google')
                // /auth/callback finishes this sign-in, so it takes `next` from here.
                holdReturnPath(searchParams.get('next'))
                window.location.href = ssoStartUrl('google')
              }}
            >
              {ssoLoading === 'google' ? (
                <Loader2 className="me-2 h-4 w-4 animate-spin" />
              ) : (
                <svg className="me-2 h-4 w-4" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              )}
              {t('auth.google')}
              {lastUsed === 'google' && <LastUsedBadge />}
            </Button>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-6 space-y-3 text-center">
          <p className="text-sm text-muted-foreground">
            {t('auth.noAccount')}{' '}
            <Link
              href="/auth/register"
              className="text-primary font-medium hover:underline"
            >
              {t('auth.signUp')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

// Wrap in Suspense: LoginPageInner calls useSearchParams (to read `next`),
// which Next 16 requires to be inside a Suspense boundary. The fallback
// mirrors the auth-gate loader so there's no UI flash on the boundary.
/** The last-used method never changes while the login page is mounted. */
const NO_STORE_SUBSCRIBE = () => () => {}

/**
 * A sign-in method tab: an equal share of the row, the label centred and
 * allowed to wrap (whitespace-normal overrides TabsTrigger's nowrap), every
 * tab as tall as the tallest (h-auto in a stretched row).
 */
const METHOD_TAB_CLASS =
  'h-auto min-h-8 min-w-0 flex-1 gap-1 px-1.5 py-1.5 text-center text-xs leading-tight whitespace-normal sm:gap-1.5 sm:px-2 sm:text-sm'

/**
 * "Last used" marker, built on the shared <Badge variant="outline">.
 *
 * Soft blue rather than a solid fill: it sits inside a control the operator is
 * meant to click, and a solid block outweighs the primary Sign in button.
 *
 * `floating` is for the TABS. A tab is ~131px and its short label already
 * fills it, so an inline badge would push it onto more lines. Absolutely
 * positioned, it costs the label no width at all — TabsTrigger is already
 * `relative`, and the tab row carries `mt-3` so this is not clipped.
 * The SSO buttons are ~194px, so those stay inline. Its words are the
 * catalogue's (auth.methodTabs.lastUsed): it was English on every page.
 */
function LastUsedBadge({ floating = false }: { floating?: boolean }) {
  const { t } = useTranslation()
  return (
    <Badge
      variant="outline"
      className={cn(
        'h-[15px] border-blue-200 bg-blue-50 px-1.5 text-[8.5px] leading-none text-blue-700',
        'dark:border-blue-400/45 dark:bg-blue-600/20 dark:text-blue-300',
        floating &&
          'absolute -top-2 left-1/2 z-10 -translate-x-1/2 ring-[3px] ring-background',
      )}
    >
      {t('auth.methodTabs.lastUsed')}
    </Badge>
  )
}

/**
 * A tab panel is a tab stop of its own (Radix gives it tabIndex 0), so it shows
 * the focus ring every other control here shows (accessibility review,
 * 2026-10-05: the Email + Password panel took focus with no visible ring).
 */
const TAB_PANEL_FOCUS =
  'rounded-md focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'

export default function LoginPage() {
  return (
    <Suspense fallback={<BrandedLoader />}>
      <LoginPageInner />
    </Suspense>
  )
}
